import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';

import TouchableElement from '@components/common/touchableElement';
import ColorWrapper from '@styles/colors';
import ModalWrapper from '@components/modal/ModalWrapper';
import Svg from '@styles/icons/svg';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { useContainerPositionRef } from '@v2/pages/common/hooks/useContainerPositionRef';

import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';

type Item = { label: string | number; value: string | number };

interface SelectProps {
  items: Item[];
  selected?: Item['value'];
  onChange: (item: Item) => void;
  C: ColorFunction;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const Select: React.FC<SelectProps> = ({ C, items, onChange, selected, scale = initialScale, font = initialFont }) => {
  const [containerRef, containerPosition] = useContainerPositionRef();

  const [open, setOpen] = useState<boolean>(false);

  const selectedItem = useMemo(() => {
    return items.find((i) => i.value === selected);
  }, [items, selected]);

  const handleChange = useCallback(
    (item: Item, isSelected: boolean) => () => {
      if (isSelected) {
        setOpen(false);
        return;
      } else {
        onChange(item);
        setOpen(false);
      }
    },
    [onChange]
  );

  return (
    <>
      <TouchableElement onPress={() => setOpen(true)}>
        {({ boxStyle }) => {
          return (
            <View
              ref={containerRef}
              style={[
                boxStyle,
                {
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: scale(4),
                  cursor: 'pointer',
                },
              ]}
            >
              <Text
                style={[
                  font['Subhead 2'],
                  {
                    color: C('Label/Light/Secondary'),
                  },
                ]}
              >
                {selectedItem?.label ?? '-'}
              </Text>
              <Svg icon="chevronBottom" color={C('Gray/Light/2')} width={scale(12)} height={scale(10)} />
            </View>
          );
        }}
      </TouchableElement>
      {open && (
        <ModalWrapper
          C={C}
          isVisible={open}
          onBackdropPress={() => setOpen(false)}
          withPositionedContainer
          positionedContainerStyles={{
            right: containerPosition.right,
            top: containerPosition.top,
          }}
          scale={scale}
        >
          <ScrollView>
            {items.map((item) => {
              const isSelected = item.value === selected;

              return (
                <TouchableElement key={item.value} onPress={handleChange(item, isSelected)}>
                  {({}) => {
                    return (
                      <View
                        style={{
                          paddingHorizontal: scale(12),
                          paddingVertical: scale(6.5),
                          backgroundColor: isSelected ? C('Selected/Light/Secondary') : C('Background/Light/Modal Highlight'),
                          cursor: 'pointer',
                        }}
                      >
                        <Text style={[font['Subhead 2'], { color: C('Label/Light/Secondary') }]}>{item.label}</Text>
                      </View>
                    );
                  }}
                </TouchableElement>
              );
            })}
          </ScrollView>
        </ModalWrapper>
      )}
    </>
  );
};

export default ColorWrapper(Select);
